import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as specApi from '../services/specificationService';
import type { CreateSpecData, UpdateSpecData } from '../services/specificationService';
import type { ImportTC } from '@maios/shared';
import { testCaseKeys } from './useTestCases';

export const specKeys = {
  all: (projectId: string) => ['specs', projectId] as const,
  detail: (projectId: string, specId: string) => ['specs', projectId, specId] as const,
  history: (projectId: string, specId: string) => ['specs', projectId, specId, 'history'] as const,
  diff: (projectId: string, specId: string, v1: number, v2: number) =>
    ['specs', projectId, specId, 'diff', v1, v2] as const,
};

export function useSpecs(projectId: string, params?: { requirementId?: string; status?: string }) {
  return useQuery({
    queryKey: [...specKeys.all(projectId), params],
    queryFn: () => specApi.fetchSpecs(projectId, params),
    enabled: !!projectId,
  });
}

export function useSpec(projectId: string, specId: string) {
  return useQuery({
    queryKey: specKeys.detail(projectId, specId),
    queryFn: () => specApi.fetchSpec(projectId, specId),
    enabled: !!projectId && !!specId,
  });
}

export function useCreateSpec(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSpecData) => specApi.createSpec(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: specKeys.all(projectId) });
    },
  });
}

export function useUpdateSpec(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { specId: string; data: UpdateSpecData }) =>
      specApi.updateSpec(projectId, params.specId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: specKeys.detail(projectId, vars.specId) });
      queryClient.invalidateQueries({ queryKey: specKeys.all(projectId) });
    },
  });
}

export function useDeleteSpec(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (specId: string) => specApi.deleteSpec(projectId, specId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: specKeys.all(projectId) });
    },
  });
}

export function useGenerateTestCases(projectId: string) {
  return useMutation({
    mutationFn: (specId: string) => specApi.generateTestCases(projectId, specId),
  });
}

export function useSaveTestCases(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { specId: string; testCases: specApi.GeneratedTestCase[] }) =>
      specApi.saveTestCases(projectId, params.specId, params.testCases),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: specKeys.detail(projectId, vars.specId) });
      // CHG-009: TC 자동 생성 후 TC 목록 자동 갱신
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
    },
  });
}

export function useSpecHistory(projectId: string, specId: string) {
  return useQuery({
    queryKey: specKeys.history(projectId, specId),
    queryFn: () => specApi.fetchSpecHistory(projectId, specId),
    enabled: !!projectId && !!specId,
  });
}

export function useSpecDiff(projectId: string, specId: string, v1: number, v2: number) {
  return useQuery({
    queryKey: specKeys.diff(projectId, specId, v1, v2),
    queryFn: () => specApi.fetchSpecDiff(projectId, specId, v1, v2),
    enabled: !!projectId && !!specId && v1 > 0 && v2 > 0 && v1 !== v2,
  });
}

// --- TC Import ---

export function useImportPreviewJSON(projectId: string) {
  return useMutation({
    mutationFn: (params: { specId: string; jsonContent: string }) =>
      specApi.importPreviewJSON(projectId, params.specId, params.jsonContent),
  });
}

export function useImportPreviewXLSX(projectId: string) {
  return useMutation({
    mutationFn: (params: { specId: string; file: File }) =>
      specApi.importPreviewXLSX(projectId, params.specId, params.file),
  });
}

export function useImportConfirm(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { specId: string; testCases: ImportTC[] }) =>
      specApi.importConfirm(projectId, params.specId, params.testCases),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testCaseKeys.all(projectId) });
    },
  });
}

// --- Spec Import ---

export function useSpecImportPreviewJSON(projectId: string) {
  return useMutation({
    mutationFn: (params: { data: unknown; requirementId?: string }) =>
      specApi.specImportPreviewJSON(projectId, params.data, params.requirementId),
  });
}

export function useSpecImportPreviewXLSX(projectId: string) {
  return useMutation({
    mutationFn: (params: { file: File; requirementId?: string }) =>
      specApi.specImportPreviewXLSX(projectId, params.file, params.requirementId),
  });
}

export function useSpecImportConfirm(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { items: specApi.SpecImportConfirmItem[]; requirementId: string }) =>
      specApi.specImportConfirm(projectId, params.items, params.requirementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: specKeys.all(projectId) });
    },
  });
}
